// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    customerProductNumber?: string;
    specNumber?: string;
    productNumber: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    showSpecNumber?: boolean;
    showProductNumber?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    modelNumber?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;

    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;

    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;

    specNumberGridItem?: GridItemProps;
    specNumberLabelText?: TypographyPresentationProps;
    specNumberValueText?: TypographyPresentationProps;

    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;

    labelText?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 5 },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    customerProductNumber,
    modelNumber,
    specNumber,
    productNumber,
    manufacturerItem,
    showSpecNumber = true,
    showCustomerName = true,
    showManufacturerItem = true,
    showProductNumber = true,
    extendedStyles,
    showLabel,
    customSettings,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const displaySpecNumber = showSpecNumber && specNumber;
    const displayErpNumber = showProductNumber && productNumber;
    const numberOfItems = 2 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = 6;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth, desktopGridItemWidth]
        : 12;

    const showModelNumber = (customSettings as any).showModelNumber?.toLowerCase() === "true";
    return (
        <GridContainer {...styles.container}>
            {displayCustomerNumber && (
                // xng-change - adding as="li" to make it more semantic
                <GridItem as="li" width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && (
                        <Typography className="product-attr-label" {...styles.labelText}>
                            {siteMessage("Custom_Your_Part_#", "Your Part #")}&nbsp;
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography className="product-attr-label" {...styles.valueText}>
                                {siteMessage("Custom_Your_Part_#", "Your Part #")}&nbsp;
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography
                        className="product-attr-value"
                        {...styles.valueText}
                        data-test-selector="customerProductNumber"
                    >
                        {customerProductNumber}
                    </Typography>
                </GridItem>
            )}
            {displaySpecNumber && (
                // xng-change - adding as="li" to make it more semantic
                <GridItem as="li" width={gridItemWidth as GridWidths} {...styles.specNumberGridItem}>
                    {showLabel && (
                        <Typography className="product-attr-label" {...styles.labelText}>
                            {siteMessage("Custom_Spec_#", "Spec #")}&nbsp;
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography className="product-attr-label" {...styles.valueText}>
                                {siteMessage("Custom_Spec_#", "Spec #")}&nbsp;
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography className="product-attr-value" {...styles.valueText}>
                        {specNumber}
                    </Typography>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                // xng-change - adding as="li" to make it more semantic
                <GridItem as="li" width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && (
                        <Typography className="product-attr-label" {...styles.labelText}>
                            {siteMessage("Custom_MFR_#", "MFR #")}&nbsp;
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography className="product-attr-label" {...styles.valueText}>
                                {siteMessage("Custom_MFR_#", "MFR #")}&nbsp;
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography
                        className="product-attr-value"
                        {...styles.valueText}
                        data-test-selector="manufacturerItem"
                    >
                        {manufacturerItem}
                    </Typography>
                </GridItem>
            )}
            {displayErpNumber && (
                // xng-change - adding as="li" to make it more semantic
                <GridItem as="li" width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && (
                        <Typography className="product-attr-label" {...styles.labelText}>
                            {siteMessage("Custom_Part_#", "EIS Part #")}&nbsp;
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography className="product-attr-label" {...styles.valueText}>
                                {siteMessage("Custom_Part_#", "EIS Part #")}&nbsp;
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography className="product-attr-value" {...styles.valueText}>
                        {productNumber}
                    </Typography>
                </GridItem>
            )}
            {showModelNumber && modelNumber && (
                // xng-change - adding as="li" to make it more semantic
                <GridItem as="li" width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && (
                        <Typography className="product-attr-label" {...styles.labelText}>
                            {siteMessage("Custom_Model_#", "Model #")}&nbsp;
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography className="product-attr-label" {...styles.valueText}>
                                {siteMessage("Custom_Model_#", "Model #")}&nbsp;
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography className="product-attr-value" {...styles.valueText}>
                        {modelNumber}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(ProductPartNumbers);
